// Post detail page component

import { useState } from 'react'
import { useParams, useNavigate, Navigate, Link } from 'react-router'
import { usePost } from '@/hooks/usePost'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatRelativeTime, formatNumber, formatReputation } from '@/utils/formatters'
import { ArrowLeft, MessageCircle, ArrowUp, DollarSign, RefreshCw, Repeat2, MoreVertical, Edit, Trash2 } from 'lucide-react'
import { CommentList } from '@/components/comment/CommentList'
import { CommentForm } from '@/components/comment/CommentForm'
import { MarkdownRenderer } from '@/components/post/MarkdownRenderer'
import { EditPostDialog } from '@/components/post/EditPostDialog'
import { DeletePostDialog } from '@/components/post/DeletePostDialog'
import { useAuthStore } from '@/stores/authStore'
import { useResteem, useHasResteemed, useCanResteem } from '@/hooks/useResteem'
import { toast } from 'sonner'

export default function PostDetailPage() {
  const { atAuthor, permlink } = useParams<{ atAuthor: string; permlink: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Extract author from @author format
  const author = atAuthor?.startsWith('@') ? atAuthor.slice(1) : ''

  const { data: post, isLoading, isError, refetch } = usePost({
    author,
    permlink: permlink || '',
    enabled: !!author && !!permlink,
  })

  // Resteem hooks
  const { data: hasResteemed, isLoading: isCheckingResteem } = useHasResteemed({
    author,
    permlink: permlink || '',
  })
  const { canResteem } = useCanResteem({ author })
  const resteemMutation = useResteem()

  const isOwnPost = user?.username === author

  const handleResteem = () => {
    if (!user) {
      toast.error('Please login to resteem posts')
      return
    }

    if (hasResteemed) {
      toast.info('You have already resteemed this post')
      return
    }

    resteemMutation.mutate(
      { author, permlink: permlink || '' },
      {
        onSuccess: () => {
          toast.success('Post has been shared to your blog')
        },
        onError: (error) => {
          toast.error('Resteem failed', {
            description: error.message,
          })
        },
      }
    )
  }

  // Redirect if URL doesn't have correct format
  if (!atAuthor?.startsWith('@') || !permlink) {
    return <Navigate to="/" replace />
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onBack={() => navigate(-1)} />
        <main className="container max-w-3xl px-4 py-8">
          <PostSkeleton />
        </main>
      </div>
    )
  }

  if (isError || !post) {
    return (
      <div className="min-h-screen bg-background">
        <Header onBack={() => navigate(-1)} />
        <main className="container max-w-3xl px-4 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <p className="mb-4 text-lg text-muted-foreground">
                게시글을 찾을 수 없습니다
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="mr-2 h-4 w-4" />
                다시 시도
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header onBack={() => navigate(-1)} />

      <main className="container max-w-3xl px-4 py-8">
        <article>
          {/* Author Info */}
          <div className="flex items-center gap-4 mb-6">
            <Link to={`/@${post.author}`}>
              <Avatar className="h-12 w-12">
                <AvatarImage
                  src={`https://steemitimages.com/u/${post.author}/avatar`}
                  alt={post.author}
                />
                <AvatarFallback className="text-lg">
                  {post.author[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex-1">
              <Link
                to={`/@${post.author}`}
                className="font-semibold hover:underline"
              >
                {post.author}
              </Link>
              <span className="ml-2 text-sm text-muted-foreground">
                ({formatReputation(post.authorReputation)})
              </span>
              <p className="text-sm text-muted-foreground">
                {formatRelativeTime(post.created)}
              </p>
            </div>
            {/* Edit/Delete dropdown for own posts */}
            {isOwnPost && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    수정
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    삭제
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Title */}
          {post.title && (
            <h1 className="text-3xl font-bold mb-6">{post.title}</h1>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="text-sm bg-secondary text-secondary-foreground px-3 py-1 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* Body Content */}
          <div className="max-w-none mb-8">
            <MarkdownRenderer content={post.body} />
          </div>

          {/* Engagement Stats */}
          <div className="flex items-center flex-wrap gap-3 sm:gap-6 py-4 border-t border-b text-muted-foreground">
            <div className="flex items-center gap-2">
              <ArrowUp className="h-5 w-5" />
              <span>{formatNumber(post.upvoteCount)} votes</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              <span>{formatNumber(post.commentCount)} comments</span>
            </div>
            {/* Resteem button */}
            {!isOwnPost && (
              <button
                onClick={handleResteem}
                disabled={resteemMutation.isPending || isCheckingResteem || hasResteemed || !canResteem}
                className={`flex items-center gap-2 transition-colors ${
                  hasResteemed
                    ? 'text-green-500'
                    : canResteem
                      ? 'hover:text-green-500'
                      : 'opacity-50 cursor-not-allowed'
                }`}
                title={hasResteemed ? 'Already resteemed' : canResteem ? 'Resteem to your blog' : 'Cannot resteem'}
              >
                <Repeat2 className={`h-5 w-5 ${resteemMutation.isPending ? 'animate-spin' : ''}`} />
                <span>{hasResteemed ? 'Resteemed' : 'Resteem'}</span>
              </button>
            )}
            {post.payout > 0 && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                <span>${post.payout.toFixed(2)}</span>
              </div>
            )}
          </div>

          {/* Comment Form */}
          <CommentForm
            parentAuthor={post.author}
            parentPermlink={post.permlink}
          />

          {/* Comments Section */}
          <CommentList
            author={post.author}
            permlink={post.permlink}
            commentCount={post.commentCount}
          />
        </article>

        {/* Edit/Delete Dialogs */}
        {isOwnPost && (
          <>
            <EditPostDialog
              open={editDialogOpen}
              onOpenChange={setEditDialogOpen}
              post={post}
            />
            <DeletePostDialog
              open={deleteDialogOpen}
              onOpenChange={setDeleteDialogOpen}
              post={post}
              onDeleteSuccess={() => navigate('/')}
            />
          </>
        )}
      </main>
    </div>
  )
}

function Header({ onBack }: { onBack: () => void }) {
  return (
    <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center gap-4 px-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">Post</h1>
      </div>
    </header>
  )
}

function PostSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-16" />
        </div>
      </div>
      <Skeleton className="h-10 w-3/4" />
      <div className="flex gap-2">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-14 rounded-full" />
      </div>
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    </div>
  )
}

